import React, { useEffect, useMemo, useState } from "react";
import { Download, RefreshCw, Star } from "lucide-react";

import api from "../api";

function OutstandingReport() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total_outstanding: 0, total_overdue: 0, party_count: 0 });
  const [sortBy, setSortBy] = useState("outstanding");
  const [sortDir, setSortDir] = useState("desc");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/accounts/party/outstanding-report");
      setRows(res.data.data || []);
      setSummary({
        total_outstanding: Number(res.data.total_outstanding || 0),
        total_overdue: Number(res.data.total_overdue || 0),
        party_count: Number(res.data.party_count || 0),
      });
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load outstanding report.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const sortedRows = useMemo(() => {
    const factor = sortDir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const left = a[sortBy];
      const right = b[sortBy];
      if (typeof left === "number" && typeof right === "number") {
        return (left - right) * factor;
      }
      return String(left || "").localeCompare(String(right || "")) * factor;
    });
  }, [rows, sortBy, sortDir]);

  const exportExcel = () => {
    const headers = ["Name", "Code", "Outstanding", "Overdue Amount", "Overdue Invoices", "Rating", "Credit Limit", "Blacklisted"];
    const csv = [headers, ...sortedRows.map((row) => [
      row.name,
      row.code,
      row.outstanding,
      row.overdue_amount,
      row.overdue_invoices,
      row.payment_rating,
      row.credit_limit,
      row.is_blacklisted ? "Yes" : "No",
    ])]
      .map((line) => line.map((value) => `"${String(value ?? "").replaceAll(`"`, `""`)}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "application/vnd.ms-excel;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "outstanding-report.xls";
    link.click();
    URL.revokeObjectURL(url);
  };

  const updateSort = (field) => {
    if (sortBy === field) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortDir("desc");
    }
  };

  return (
    <div className="space-y-6">
      {error && <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

      <section className="grid gap-4 md:grid-cols-3">
        <StatCard label="Total Outstanding" value={`Rs. ${summary.total_outstanding.toFixed(2)}`} />
        <StatCard label="Total Overdue" value={`Rs. ${summary.total_overdue.toFixed(2)}`} tone="rose" />
        <StatCard label="Parties with Dues" value={summary.party_count} />
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-6 py-5">
          <div>
            <h3 className="text-xl font-bold text-slate-900">Outstanding Report</h3>
            <p className="text-sm text-slate-500">All parties with dues, overdue exposure, and payment rating flags.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={load} className="secondary-btn"><RefreshCw size={15} /> Refresh</button>
            <button onClick={exportExcel} className="secondary-btn"><Download size={15} /> Export Excel</button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <SortableHeader label="Party" field="name" onSort={updateSort} />
                <SortableHeader label="Outstanding" field="outstanding" onSort={updateSort} />
                <SortableHeader label="Overdue" field="overdue_amount" onSort={updateSort} />
                <SortableHeader label="Overdue Invoices" field="overdue_invoices" onSort={updateSort} />
                <SortableHeader label="Credit Limit" field="credit_limit" onSort={updateSort} />
                <SortableHeader label="Rating" field="payment_rating" onSort={updateSort} />
                <th className="px-4 py-3">Flags</th>
              </tr>
            </thead>
            <tbody>
              {!loading && sortedRows.map((row) => (
                <tr key={row.id} className="border-t border-slate-100">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-900">{row.name}</div>
                    <div className="text-xs text-slate-500">{row.code}</div>
                  </td>
                  <td className="px-4 py-3 font-semibold">Rs. {Number(row.outstanding || 0).toFixed(2)}</td>
                  <td className="px-4 py-3 font-semibold text-rose-700">Rs. {Number(row.overdue_amount || 0).toFixed(2)}</td>
                  <td className="px-4 py-3">{row.overdue_invoices}</td>
                  <td className="px-4 py-3">Rs. {Number(row.credit_limit || 0).toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      {Array.from({ length: 5 }, (_, index) => (
                        <Star key={index} size={14} className={index < Number(row.payment_rating || 0) ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      {Number(row.payment_rating || 0) <= 2 && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">Low Rated</span>}
                      {row.is_blacklisted && <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700">Blacklisted</span>}
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && !sortedRows.length && (
                <tr>
                  <td colSpan="7" className="px-4 py-10 text-center text-slate-500">No outstanding parties.</td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan="7" className="px-4 py-10 text-center text-slate-500">Loading report...</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <style>{`
        .secondary-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          border-radius: 0.75rem;
          padding: 0.65rem 1rem;
          font-size: 0.875rem;
          font-weight: 600;
          border: 1px solid rgb(203 213 225);
          background: white;
          color: rgb(51 65 85);
        }
      `}</style>
    </div>
  );
}

function StatCard({ label, value, tone = "slate" }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-2 text-2xl font-bold ${tone === "rose" ? "text-rose-700" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}

function SortableHeader({ label, field, onSort }) {
  return (
    <th className="px-4 py-3">
      <button onClick={() => onSort(field)} className="font-semibold text-slate-500">{label}</button>
    </th>
  );
}

export default OutstandingReport;
